import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaMagic, FaMapMarkerAlt, FaClock, FaRoute, FaUtensils, FaStar } from "react-icons/fa";
import { supabase } from "../supabaseClient";

type Props = {
  cityLabel?: string;
  lat?: number | null;
  lng?: number | null;
};

const QUICK_ACTIONS = [
  { id: "today", label: "Que faire aujourd’hui", icon: FaStar, prompt: "Propose un mini programme pour aujourd’hui" },
  { id: "4h", label: "Itinéraire 4h", icon: FaRoute, prompt: "Crée un itinéraire optimisé de 4 heures" },
  { id: "food", label: "Où manger", icon: FaUtensils, prompt: "Suggère des options resto/café selon mon secteur" },
  { id: "evening", label: "Soirée", icon: FaClock, prompt: "Propose une idée de soirée sympa" }
];

export default function MiniGuideAI({ cityLabel = "Québec", lat, lng }: Props) {
  const [loading, setLoading] = useState(false);
  const [answer, setAnswer] = useState<string>("");
  const [lastAction, setLastAction] = useState<string>("");

  const contextText = useMemo(() => {
    const loc = (lat && lng) ? `Coordonnées: ${lat.toFixed(5)}, ${lng.toFixed(5)}.` : `Localisation non précisée.`;
    return `Ville/cible: ${cityLabel}. ${loc}`;
  }, [cityLabel, lat, lng]);

  const run = async (actionId: string, extra?: string) => {
    try {
      setLoading(true);
      setLastAction(actionId);
      setAnswer("");

      const action = QUICK_ACTIONS.find(a => a.id === actionId);
      const userIntent = action?.prompt ?? "Donne-moi un mini-guide utile";

      const payload = {
        cityLabel,
        lat,
        lng,
        userIntent,
        extra: extra || ""
      };

      // ✅ Appel à une Edge Function Supabase (ne met PAS la clé OpenAI dans le front)
      const { data, error } = await supabase.functions.invoke("ai-mini-guide", {
        body: payload
      });

      if (error) throw error;
      setAnswer(data?.text || "Aucune réponse pour le moment.");
    } catch (e: any) {
      setAnswer("Impossible de générer le mini-guide pour l’instant. Réessaie dans un moment.");
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative overflow-hidden rounded-[2rem] border border-white/15 bg-white/10 backdrop-blur-xl shadow-2xl">
      <div className="absolute inset-0 opacity-30 pointer-events-none bg-gradient-to-br from-indigo-500/30 via-cyan-400/10 to-blue-600/30" />
      
      <div className="relative p-6 md:p-8">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/15 text-white/90 text-[10px] font-black uppercase tracking-widest">
              <FaMagic className="text-yellow-300" /> Mini-Guide intelligent
            </div>
            <h3 className="mt-3 text-2xl md:text-3xl font-black text-white tracking-tight">
              Ton guide instantané, en français
            </h3>
            <p className="mt-2 text-white/75 text-sm md:text-base leading-relaxed">
              Conseils concrets, itinéraires courts, idées de bouffe et spots incontournables — adaptés à ton moment et ton secteur.
            </p>
            <div className="mt-3 flex items-center gap-2 text-white/70 text-xs font-bold">
              <FaMapMarkerAlt className="text-cyan-200" />
              <span>{contextText}</span>
            </div>
          </div>

          <button
            onClick={() => run("today")}
            className="shrink-0 hidden md:flex items-center gap-2 px-4 py-2 rounded-2xl bg-white text-slate-900 font-black text-xs uppercase tracking-widest hover:bg-slate-100 transition shadow-lg"
          >
            <FaMagic className="text-indigo-600" /> Générer
          </button>
        </div>

        {/* Quick actions */}
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
          {QUICK_ACTIONS.map(a => {
            const Icon = a.icon;
            const active = lastAction === a.id;
            return (
              <button
                key={a.id}
                onClick={() => run(a.id)}
                className={`group relative overflow-hidden rounded-2xl border px-4 py-4 text-left transition shadow-sm
                  ${active ? "bg-white text-slate-900 border-white" : "bg-white/10 text-white border-white/15 hover:bg-white/15"}
                `}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-2xl flex items-center justify-center
                    ${active ? "bg-indigo-600 text-white" : "bg-white/10 text-white"}
                  `}>
                    <Icon />
                  </div>
                  <div className="font-black text-xs md:text-sm leading-tight">
                    {a.label}
                  </div>
                </div>
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition pointer-events-none bg-gradient-to-r from-indigo-500/15 to-cyan-400/10" />
              </button>
            );
          })}
        </div>

        {/* Answer */}
        <div className="mt-6">
          <AnimatePresence>
            {loading && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                className="rounded-2xl bg-black/20 border border-white/10 p-4 text-white/80 text-sm"
              >
                Génération en cours…
              </motion.div>
            )}

            {!loading && answer && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                className="rounded-2xl bg-white text-slate-900 border border-white/30 p-5 shadow-xl"
              >
                <div className="prose prose-sm md:prose-base max-w-none">
                  {answer.split("\n").map((line, i) => (
                    <p key={i} className="m-0 mb-2 last:mb-0 whitespace-pre-wrap">
                      {line}
                    </p>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Mobile CTA */}
        <button
          onClick={() => run("today")}
          className="mt-5 w-full md:hidden flex items-center justify-center gap-2 px-4 py-4 rounded-2xl bg-white text-slate-900 font-black text-xs uppercase tracking-widest hover:bg-slate-100 transition shadow-lg"
        >
          <FaMagic className="text-indigo-600" /> Générer le mini-guide
        </button>
      </div>
    </div>
  );
}
